import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { FloatingContact } from "@/components/FloatingContact";
import { buildOrganizationSchema, buildWebSiteSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { COMPANY } from "@/lib/company";

// Built once per worker instance instead of on every request (TTFB).
const LD_JSON = [
  JSON.stringify(buildOrganizationSchema()),
  JSON.stringify(buildWebSiteSchema()),
];

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => {
    const { config } = getActiveProvince();
    // Env-driven site verification meta tags. Set the matching VITE_*
    // env var (or leave blank) — the meta tag is only emitted when a
    // verification token is present, so we never ship empty/invalid tags.
    const verifications: { name: string; content: string }[] = (
      [
        ["google-site-verification", import.meta.env.VITE_GOOGLE_SITE_VERIFICATION],
        ["msvalidate.01", import.meta.env.VITE_BING_SITE_VERIFICATION],
        ["yandex-verification", import.meta.env.VITE_YANDEX_VERIFICATION],
        ["p:domain_verify", import.meta.env.VITE_PINTEREST_VERIFICATION],
        ["facebook-domain-verification", import.meta.env.VITE_FACEBOOK_DOMAIN_VERIFICATION],
      ] as const
    )
      .filter(([, v]) => typeof v === "string" && v.length > 0)
      .map(([name, content]) => ({ name, content: content as string }));
    return ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0f1b3d" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" },
      { property: "og:site_name", content: `${COMPANY.brandName} ${config.nameEn}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
      ...verifications,
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/webp", href: "/favicon.webp" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      // Fonts are self-hosted (public/fonts) — no cross-origin render-blocking
      // stylesheet. Preload only the two subsets needed for first paint.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/prompt-thai-400.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/prompt-latin-700.woff2",
        crossOrigin: "anonymous",
      },
      // Performance: warm up TCP/TLS to external origins we link to from
      // every page (LINE deep-links, WhatsApp, Google Maps embeds in branches).
      { rel: "dns-prefetch", href: "https://line.me" },
      { rel: "dns-prefetch", href: "https://wa.me" },
      { rel: "dns-prefetch", href: "https://maps.google.com" },
      { rel: "preconnect", href: "https://line.me", crossOrigin: "" },
      { rel: "preconnect", href: "https://wa.me", crossOrigin: "" },
    ],
    scripts: LD_JSON.map((children) => ({ type: "application/ld+json", children })),
  });
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="th">
      <head>
        <HeadContent />
      </head>
      <body>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground"
        >
          ข้ามไปยังเนื้อหาหลัก
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <FloatingContact />
    </QueryClientProvider>
  );
}
